/** New Item Requests (Warehouse > Create New Item) and the G/L rule year copy. */

export interface ItemFieldMeta {
  field: string;
  description: string;
  mandatory: boolean;
  size: number;
  defaultValue: string | null;
  linkedTable: string | null;
  values: Array<{ value: string; description: string }>;
  required: boolean;
}

export interface UomGroupRef {
  entry: number;
  code: string;
  name: string;
  baseUom: number | null;
  uoms: Array<{ entry: number; code: string; name: string }>;
}

export interface ItemRequestCapabilities { admin: boolean; finance: boolean; procurement: boolean; viewAll: boolean }

export interface ItemRequestLookups {
  itemGroups: Array<{ code: number; name: string }>;
  uomGroups: UomGroupRef[];
  fields: ItemFieldMeta[];
  /** The label of every item UDF of the company, for fields a request carries besides the form's. */
  labels?: Record<string, string>;
  period: { code: string; year: number } | null;
  capabilities: ItemRequestCapabilities;
}

export type GLAccounts = Record<string, string | null>;

export interface GLRow {
  key: string;
  label: string;
  warehouseType: string | null;
  warehouseCode: string | null;
  warehouseCount: number;
  rule: { absoluteEntry: number; code: string; description: string; itemSpecific: boolean } | null;
  accounts: GLAccounts;
  status: 'complete' | 'partial' | 'none';
}

export interface GLPreview {
  rows: GLRow[];
  conditionalRules: number;
  period: string;
  year: number;
  accountNames: Record<string, string | null>;
}

export interface GLOverride {
  key: string;
  label: string;
  warehouseType: string | null;
  warehouseCode: string | null;
  accounts: GLAccounts;
}

/** An item-only rule PRPO creates in SAP; `kept` marks a warehouse that keeps the accounts Finance saw for it. */
export interface PlannedItemRule extends GLOverride { kept: boolean }

export type ItemRequestStatus =
  | 'Draft' | 'PendingFinance' | 'PendingProcurement' | 'Creating' | 'Created' | 'Failed' | 'Returned' | 'Rejected' | 'Cancelled';

export interface ItemRequestRow {
  requestid: number;
  requestno: string;
  companyid: number;
  status: ItemRequestStatus;
  itemcode: string | null;
  itemname: string;
  itemsgroupcode: number | null;
  itemclass: string | null;
  sapitemcode: string | null;
  createdby: number;
  createdbyname: string | null;
  createddate: string;
  updateddate: string;
}

export interface ItemRequestHistoryRow {
  historyid: number;
  action: string;
  fromstatus: string | null;
  tostatus: string | null;
  comment: string | null;
  details: unknown;
  createddate: string;
  username: string | null;
}

export interface ItemRequestDetail extends ItemRequestRow {
  foreignname: string | null;
  uomgroupentry: number | null;
  pricingunit: number | null;
  inventoryitem: boolean;
  salesitem: boolean;
  purchaseitem: boolean;
  active: boolean;
  barcode: string | null;
  udfs: Record<string, string | null>;
  glaccounts: {
    period: string;
    year: number;
    overrides: GLOverride[];
    reviewed: Array<Pick<GLRow, 'key' | 'label' | 'rule' | 'accounts' | 'status'> & Partial<Pick<GLRow, 'warehouseType' | 'warehouseCode'>>>;
    /** The rules PRPO creates on approval (requests reviewed since Oct 2026). */
    itemRules?: PlannedItemRule[];
    accountNames?: Record<string, string | null>;
  } | null;
  glyear: string | null;
  saprules: Array<{ key: string; label: string; status: string; absoluteEntry: number | null; kept?: boolean; error?: string }> | null;
  saperror: string | null;
  submitteddate: string | null;
  financeby: number | null;
  financebyname: string | null;
  financedate: string | null;
  approvedby: number | null;
  approvedbyname: string | null;
  approveddate: string | null;
  createdinsapdate: string | null;
  history: ItemRequestHistoryRow[];
  permissions: {
    canEdit: boolean;
    canCancel: boolean;
    canFinanceReview: boolean;
    canProcurementApprove: boolean;
    canRetry: boolean;
  };
}

/** The accounts of an inventory rule, as the review screen shows them (same order as the server). */
export const GL_ACCOUNT_FIELDS: ReadonlyArray<{ field: string; label: string }> = [
  { field: 'InventoryAccount', label: 'Inventory' },
  { field: 'CostAccount', label: 'Cost (consumption)' },
  { field: 'PriceDifferenceAcc', label: 'Price difference' },
  { field: 'DecreasingAccount', label: 'Inventory offset: decrease' },
  { field: 'IncreasingAccount', label: 'Inventory offset: increase' },
  { field: 'GLDecreaseAcct', label: 'G/L decrease' },
  { field: 'GLIncreaseAcct', label: 'G/L increase' },
  { field: 'ExpensesAccount', label: 'Expense' },
];

export const ITEM_REQUEST_STATUS: Record<ItemRequestStatus, { label: string; color: 'default' | 'info' | 'warning' | 'success' | 'error' }> = {
  Draft: { label: 'Draft', color: 'default' },
  PendingFinance: { label: 'Finance review', color: 'info' },
  PendingProcurement: { label: 'Procurement Manager', color: 'info' },
  Creating: { label: 'Creating in SAP', color: 'warning' },
  Created: { label: 'Created in SAP', color: 'success' },
  Failed: { label: 'SAP refused', color: 'error' },
  Returned: { label: 'Returned', color: 'warning' },
  Rejected: { label: 'Rejected', color: 'error' },
  Cancelled: { label: 'Cancelled', color: 'default' },
};

/**
 * The warehouse rows that keep the accounts Finance saw when Finance sets accounts for their
 * warehouse type: SAP checks the item code before the warehouse code, so the item's rule for the
 * type would otherwise replace their own rule (warehouse 01 in KSA). The server works out the same
 * (glAdvancedRules.plannedItemRules) and creates an item rule for each of them.
 */
export function keptWarehouseKeys(rows: Array<Pick<GLRow, 'key' | 'warehouseType' | 'warehouseCode' | 'accounts'>>,
  overrides: Record<string, GLOverride>): string[] {
  const byType = new Map<string, GLOverride>();
  for (const o of Object.values(overrides)) {
    if (!o.warehouseCode && o.warehouseType) byType.set(o.warehouseType.toUpperCase(), o);
  }
  const same = (a: GLAccounts, b: GLAccounts) => GL_ACCOUNT_FIELDS.every(({ field }) => String(a?.[field] ?? '').trim() === String(b?.[field] ?? '').trim());
  return rows.filter((r) => {
    const override = r.key.startsWith('whs:') && r.warehouseType ? byType.get(r.warehouseType.toUpperCase()) : undefined;
    return !!override && !overrides[r.key] && Object.values(r.accounts || {}).some(Boolean) && !same(r.accounts, override.accounts);
  }).map((r) => r.key);
}

/** SAP's field labels, with the screen's own wording where SAP's label is misspelt. */
export const FIELD_LABELS: Record<string, string> = {
  U_MAIN_CATE: 'Main Category',
  U_SUB_CAT: 'Category',
  U_ITEM_TYPE: 'Item Type',
  U_Class: 'Classification',
  U_USE: 'Using For',
  U_AUX_Code: 'Auxiliary',
  U_BRAND: 'Brand',
  U_Description1: 'Description 1',
  U_Description2: 'Description 2',
  U_PartNumber2: 'Additional Part Number',
};

export interface GLRuleYear { absEntry: number; code: string; year: number; begin: string; rules: number | null }

export interface GLRuleCopyPreview {
  fromPeriod: string;
  toPeriod: string;
  sourceCount: number;
  targetCount: number;
  toCopyCount: number;
  alreadyCount: number;
  byClassification: Array<{ classification: string; count: number }>;
  toCopy: Array<{ absoluteEntry: number; code: string; description: string; classification: string; warehouseType: string; scope: string }>;
}

export interface GLRuleCopyRun {
  runid: number;
  companyid: number;
  fromperiod: string;
  toperiod: string;
  status: 'Running' | 'Completed' | 'CompletedWithErrors' | 'Failed';
  total: number;
  copied: number;
  skipped: number;
  failed: number;
  results?: Array<{ absoluteEntry: number; code: string; description: string; classification: string; warehouseType: string; scope: string; status: 'copied' | 'skipped' | 'failed'; newAbsoluteEntry?: number | null; error?: string }>;
  error: string | null;
  starteddate: string;
  finisheddate: string | null;
  startedbyname: string | null;
}
